import React from 'react';
import { observer } from 'mobx-react';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { ISortOptions, ITheadSort, ListLayout } from '../../../../../global/tables/table-list-layout';
import OECheckbox from '../../../../../global/oe-checkbox/oe-checkbox';
import Text from '../../../../../global/text/text';
import { DateTimeFormatter } from '../../../../../../infrastructure/implementations/date-formatter';
import MeetingArtifactListItemDay from '../../_meeting-artifact-list-item/meeting-artifact-llist-day-item/meeting-artifact-list-day-item';
import { IRequestMeetingArtifactDTO, TRequestArtifactsTab } from '../../../../../../api/artifacts.dao';
import { artifactMp4RecordingTypes, artifactMp4Values, requestArtifactsEmptyListMessage } from '../../../../../../constants/artifacts.constants';
import { MEETINGS_TEXT } from '../../../../../../constants/common-labels.constants';

export interface IRequestArtifactsMeetingsTableLayoutProps {
    intl: IntlShape;
    eventTimeZone: string;
    meetingsList: IRequestMeetingArtifactDTO[];
    isAllMeetingsChecked: boolean;
    handleArtifactMeetingCheckBox: (meetingId?: number) => void;
    selectedMeetingIds: Set<number>;
    selectedTab: TRequestArtifactsTab;
}

const dateTimeFormatter = IoC.Container.get<DateTimeFormatter>(DateTimeFormatter);

const RequestArtifactsMeetingsTableLayout = observer(({
    intl,
    isAllMeetingsChecked,
    meetingsList,
    eventTimeZone,
    selectedMeetingIds,
    handleArtifactMeetingCheckBox,
    selectedTab,
}: IRequestArtifactsMeetingsTableLayoutProps) => {
    const isEmptyList = meetingsList.length === 0;

    const theads: ITheadSort<string>[] = [
        {
            title: (<OECheckbox
                checked={isAllMeetingsChecked}
                onChange={()=> handleArtifactMeetingCheckBox()}
                classNames='oe-form-field__checkbox'
                isDisabled={isEmptyList}
            />),
            width: '60px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.dateAndTime',
                description: 'Label text',
                defaultMessage: 'Date & Time'
            }),
            id: 'dataTime',
            width: '170px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingTopic',
                defaultMessage: 'Topic',
                description: 'Label text',
            }),
            id: 'topic',
            width: '180px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingPresenters',
                defaultMessage: 'Presenters',
                description: 'Label text',
            }),
            id: 'presenters',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.reports.meetingType',
                defaultMessage: 'Meeting Type',
                description: 'Label text',
            }),
            id: 'type',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.artifacts.resolution',
                defaultMessage: 'Resolution',
                description: 'Label text',
            }),
            id: 'resolution',
            width: '150px'
        },
        {
            title: intl.formatMessage({
                id: 'oe.artifacts.format',
                defaultMessage: 'Format',
                description: 'Label text',
            }),
            id: 'format',
            width: '150px'
        },
    ];

    const sortOptions: ISortOptions<string> = {
        theads: theads
    };

    return (
        <section>
            <h3>{intl.formatMessage(MEETINGS_TEXT)}</h3>
            <ListLayout
                sortOptions={sortOptions}
            >
                {
                    meetingsList.map(meeting => {
                        const presenters = meeting.presenters.map(p => p.name).join(', ');
                        return (
                            <tr
                                key={meeting.meetingId}
                                onDoubleClick={() => handleArtifactMeetingCheckBox(meeting.meetingId)}
                            >
                                <td className='hoverable'>
                                    <OECheckbox
                                        checked={selectedMeetingIds.has(meeting.meetingId)}
                                        onChange={() => handleArtifactMeetingCheckBox(meeting.meetingId)}
                                        classNames='oe-form-field__checkbox'
                                    />
                                </td>
                                <td className='hoverable' >
                                    <MeetingArtifactListItemDay
                                        eventTimeZone={eventTimeZone}
                                        startDateTime={new Date(meeting.startDateTime)}
                                        dateTimeFormatter={dateTimeFormatter}
                                    />
                                </td>
                                <Text
                                    className='hoverable'
                                    tag='td'
                                    message={meeting.topic}
                                    withEllipsis
                                />
                                <Text
                                    className='hoverable'
                                    tag='td'
                                    message={presenters}
                                    withEllipsis
                                />
                                <Text
                                    className='hoverable'
                                    tag='td' message={meeting.type}
                                    withEllipsis
                                />
                                <Text
                                    className='hoverable'
                                    tag='td'
                                    message={artifactMp4Values(intl).find(resolution => resolution.value === meeting.resolution)?.label || ''}
                                    withEllipsis
                                />
                                <Text
                                    className='hoverable'
                                    tag='td'
                                    message={artifactMp4RecordingTypes(intl).find(r => r.value === meeting.recordingType)?.textInTable || ''}
                                    withEllipsis
                                />
                            </tr>
                        );
                    })
                }

            </ListLayout>
            {
                isEmptyList &&
                <div className='empty-list-message'>
                    { requestArtifactsEmptyListMessage(intl)[selectedTab] }
                </div>
            }
        </section>
    );

});

export default RequestArtifactsMeetingsTableLayout;